/**
 * The report page header's actions. The owner gets Share and a menu (rename,
 * save a copy, delete); anyone else who may copy it gets Save as my own.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  IconButton,
  Input,
  Modal,
  Popover,
  Skeleton,
  Table,
} from '@servicecompressionllc/sc-design-system';
import { Copy, Ellipsis, UserPlus } from '@servicecompressionllc/sc-design-system/icons';
import { Toggle } from '../../components/Toggle.js';
import { useReportActions, useShareCandidates } from './useReportActions.js';
import type { ReportTemplateDetail } from './useReportTemplatePageLogic.js';

const errorText = (error: Error | null): string | null => error?.message ?? null;

export const ReportActions = ({
  template: t,
  tenantName,
}: {
  template: ReportTemplateDetail;
  tenantName: string;
}) => {
  const actions = useReportActions(t.id);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState<'share' | 'rename' | 'delete' | null>(null);
  const [name, setName] = useState(t.name);
  const close = () => {
    setDialog(null);
    actions.rename.reset();
    actions.remove.reset();
  };
  const openRename = () => {
    setName(t.name);
    setDialog('rename');
  };

  if (!t.access.manage) {
    if (!t.access.copy) return null;
    return (
      <div className="flex flex-col items-end gap-1">
        <Button
          variant="secondary"
          size="sm"
          className="gap-1.5"
          disabled={actions.copy.isPending}
          onClick={() => actions.copy.mutate()}
        >
          <Copy size={15} aria-hidden />
          {actions.copy.isPending ? 'Saving' : 'Save as my own'}
        </Button>
        {actions.copy.error && (
          <p role="alert" className="text-xs text-danger">
            {actions.copy.error.message}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          className="gap-1.5"
          onClick={() => setDialog('share')}
        >
          <UserPlus size={15} aria-hidden />
          Share
        </Button>
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <Popover.Trigger asChild>
            <IconButton aria-label="More actions" variant="secondary" size="sm">
              <Ellipsis />
            </IconButton>
          </Popover.Trigger>
          <Popover.Content aria-label="More actions" align="end" className="w-64">
            <ul className="py-1">
              <Popover.Item hint="Everyone sees the new name." onClick={openRename}>
                Rename
              </Popover.Item>
              <Popover.Item
                hint="A new report of yours, starting at version 1."
                onClick={() => actions.copy.mutate()}
              >
                Save a copy
              </Popover.Item>
              <Popover.Item
                className="text-danger"
                hint="Gone for you and everyone it's shared with."
                onClick={() => setDialog('delete')}
              >
                Delete
              </Popover.Item>
            </ul>
          </Popover.Content>
        </Popover>
      </div>
      {actions.copy.error && (
        <p role="alert" className="text-xs text-danger">
          {actions.copy.error.message}
        </p>
      )}

      <ShareDialog
        open={dialog === 'share'}
        onClose={close}
        template={t}
        tenantName={tenantName}
        actions={actions}
      />

      <Modal open={dialog === 'rename'} onOpenChange={(open) => !open && close()}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            actions.rename.mutate(name, { onSuccess: close });
          }}
        >
          <Modal.Header>
            <Modal.Title>Rename report</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body className="flex flex-col gap-1.5">
            <label htmlFor="report-name" className="text-xs font-semibold text-ink-subtle">
              Name
            </label>
            <Input
              id="report-name"
              value={name}
              maxLength={120}
              error={actions.rename.isError}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-xs text-ink-subtle">
              {errorText(actions.rename.error) ?? "Everyone it's shared with sees the new name."}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={actions.rename.isPending || name.trim() === '' || name.trim() === t.name}
            >
              {actions.rename.isPending ? 'Saving' : 'Save'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal open={dialog === 'delete'} onOpenChange={(open) => !open && close()}>
        <Modal.Header>
          <Modal.Title>Delete &ldquo;{t.name}&rdquo;?</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-ink">
            It disappears from Reports for you and everyone it&apos;s shared with, and it can&apos;t
            be run anymore. Copies people saved stay theirs.
          </p>
          {actions.remove.error && (
            <p role="alert" className="mt-2 text-xs text-danger">
              {actions.remove.error.message}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={actions.remove.isPending}
            onClick={() => actions.remove.mutate()}
          >
            {actions.remove.isPending ? 'Deleting' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const ShareDialog = ({
  open,
  onClose,
  template: t,
  tenantName,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  template: ReportTemplateDetail;
  tenantName: string;
  actions: ReturnType<typeof useReportActions>;
}) => {
  const candidates = useShareCandidates(t.id, open);
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const people = (candidates.data ?? [])
    .filter((p) => !q || `${p.name ?? ''} ${p.email}`.toLowerCase().includes(q))
    .sort((a, b) => Number(b.shared) - Number(a.shared));
  const busy = actions.share.isPending ? actions.share.variables.userId : null;
  const error = errorText(actions.publish.error) ?? errorText(actions.share.error);

  return (
    <Modal open={open} onOpenChange={(next) => !next && onClose()}>
      <Modal.Header>
        <Modal.Title>Share &ldquo;{t.name}&rdquo;</Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body className="flex flex-col gap-5">
        <div className="flex items-start gap-4">
          <div className="flex flex-1 flex-col gap-1">
            <span className="text-sm font-semibold text-ink">Publish to {tenantName}</span>
            <span className="text-xs text-ink-subtle">
              Everyone with report access can find it in Published, run it and save their own copy.
              Nobody is notified.
            </span>
          </div>
          <Toggle
            checked={t.visibility === 'tenant'}
            label={`Publish to ${tenantName}`}
            disabled={actions.publish.isPending}
            onChange={() => actions.publish.mutate(t.visibility !== 'tenant')}
          />
        </div>

        <div className="flex flex-col gap-2.5">
          <span className="text-xs font-semibold uppercase tracking-[0.06em] text-ink-subtle">
            People
          </span>
          <Input
            type="search"
            aria-label="Search people with report access"
            placeholder="Search people with report access"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {candidates.isLoading && <Skeleton className="h-40 w-full" />}
          {candidates.error && <p className="text-sm text-danger">{candidates.error.message}</p>}
          {candidates.data && people.length === 0 && (
            <p className="text-sm text-ink-muted">
              {q ? 'Nobody with report access matches that.' : 'Nobody else has report access yet.'}
            </p>
          )}
          {people.length > 0 && (
            <div className="max-h-64 overflow-y-auto rounded-lg border border-outline">
              <Table.Root className="w-full text-sm" aria-label="People">
                <Table.Body>
                  {people.map((p) => (
                    <Table.Row key={p.userId}>
                      <Table.Cell>
                        <span className="block font-medium text-ink">{p.name ?? p.email}</span>
                        {p.name && <span className="block text-xs text-ink-subtle">{p.email}</span>}
                      </Table.Cell>
                      <Table.Cell align="right" className="whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          {p.shared && <Badge variant="info">Shared</Badge>}
                          <Button
                            variant={p.shared ? 'ghost' : 'secondary'}
                            size="sm"
                            aria-label={`${p.shared ? 'Remove' : 'Share with'} ${p.name ?? p.email}`}
                            disabled={busy === p.userId}
                            onClick={() =>
                              actions.share.mutate({ userId: p.userId, on: !p.shared })
                            }
                          >
                            {p.shared ? 'Remove' : 'Share'}
                          </Button>
                        </span>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </div>
          )}
          {error && (
            <p role="alert" className="text-xs text-danger">
              {error}
            </p>
          )}
          <p className="text-xs text-ink-subtle">
            Only people with report access are listed. Sharing sends them a message with a link.
            Removing someone takes away their access, but a copy they saved stays theirs.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
